/** Abstract base class for all VFX primitives — enforces pool lifecycle, config injection, and cleanup. */
import Phaser from 'phaser';
import { CRISP_SCALE } from '../../ui/CrispUtil';

/** Base configuration shared by all primitives. */
export interface VfxPrimitiveConfig {
  /** CrispUtil texture radius or dimensions, in logical pixels. */
  textureSize: number;
  /** Hex tint applied to all pool Images. */
  tint: number;
  /** Phaser BlendMode (default NORMAL). */
  blendMode?: Phaser.BlendModes;
  /** Depth offset relative to the effect's anchor entity. */
  depthOffset?: number;
}

export abstract class VfxPrimitive<TConfig extends VfxPrimitiveConfig> {
  protected scene: Phaser.Scene;
  protected config: TConfig;
  protected pool: Phaser.GameObjects.Image[] = [];
  private destroyed = false;

  constructor(scene: Phaser.Scene, config: TConfig) {
    this.scene = scene;
    this.config = config;
    this.allocatePool();
  }

  /** Subclass allocates its pool Images here. Called once by constructor. */
  protected abstract allocatePool(): void;

  /** Activate the effect at/around a position or entity. */
  abstract play(x: number, y: number, depth: number): void;

  /** Halt the effect gracefully (fade out, no abrupt pop). */
  abstract stop(): void;

  /** Set the direction for directional primitives before calling play(). No-op by default. */
  setDirection(_angle: number): void {}

  /** Reposition the effect to follow a moving entity. Called every frame for sustain effects. No-op by default. */
  updatePosition(_x: number, _y: number, _depth: number, _delta: number): void {}

  /** Immediate teardown — kill tweens, destroy all GameObjects. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const img of this.pool) {
      this.scene.tweens.killTweensOf(img);
      img.destroy();
    }
    this.pool.length = 0;
  }

  /** Deactivate an Image (visible=false, alpha=0) without destroying it. */
  protected deactivate(img: Phaser.GameObjects.Image): void {
    img.setVisible(false).setAlpha(0).setActive(false);
  }

  /** Reactivate a pooled Image at a position. */
  protected activate(
    img: Phaser.GameObjects.Image,
    x: number,
    y: number,
    depth: number,
  ): void {
    img.setPosition(x, y)
      .setDepth(depth + (this.config.depthOffset ?? 0))
      .setAlpha(1)
      .setVisible(true)
      .setActive(true);
  }

  /** Create a pool Image using a CrispUtil texture key. */
  protected createPoolImage(textureKey: string): Phaser.GameObjects.Image {
    const img = this.scene.add.image(0, 0, textureKey)
      .setScale(1 / CRISP_SCALE)
      .setTint(this.config.tint)
      .setBlendMode(this.config.blendMode ?? Phaser.BlendModes.NORMAL);
    this.deactivate(img);
    this.pool.push(img);
    return img;
  }

  /** Get next inactive Image from pool, or grow if exhausted. */
  protected getFromPool(textureKey: string): Phaser.GameObjects.Image {
    const inactive = this.pool.find(img => !img.active);
    if (inactive) return inactive;
    return this.createPoolImage(textureKey);
  }
}
